import { acceptHMRUpdate, defineStore } from 'pinia';
import { unref } from 'vue';

import { PropertyOnboardingType } from '@tallo/investor/navigation';
import { usePropertyStore } from '@tallo/investor/property';

import {
  PropertyOnboardingStage,
  PropertyOnboardingStageMetaData,
  PropertyOnboardingStep,
} from '../configs/stages.enum';
import { propertyOnboardingRoutes } from '../property-onboarding.routes';
import { getPropertyOnboardingStepRouteName } from '../utils/get-property-onboarding-step-route-name.util';
import { stepValidations } from '../utils/step-validations';

interface PropertyOnboardingStaste {}

const buildingAnsweredSteps = [PropertyOnboardingStep.ADDRESS, PropertyOnboardingStep.PROPERTY_TYPE];

export const usePropertyOnboardingStore = defineStore({
  id: 'propertyOnboarding',
  state: (): PropertyOnboardingStaste => ({}),
  getters: {
    getStageRoutes() {
      return (propertyOnboardingRoutes.children || []).filter((route) => {
        return Object.values(PropertyOnboardingStage).includes(route.path as PropertyOnboardingStage);
      });
    },

    getStagesWithMetaData(): PropertyOnboardingStageMetaData[] {
      return this.getStageRoutes.map((route) => route.meta as unknown as PropertyOnboardingStageMetaData);
    },

    getStages(): PropertyOnboardingStage[] {
      return this.getStagesWithMetaData.map(({ stageName }) => stageName);
    },

    getStageSteps() {
      return (stageName: PropertyOnboardingStage): PropertyOnboardingStep[] => {
        const stageRoute = (propertyOnboardingRoutes.children || []).find((stageRoute) => {
          return stageRoute.meta?.stageName === stageName;
        });

        const steps =
          stageRoute?.children?.map((stepRoute) => stepRoute.meta?.stepName as PropertyOnboardingStep) || [];

        // A unit in a building takes its address and type from the building, so those are not asked
        return usePropertyStore().property?.buildingId
          ? steps.filter((step) => !buildingAnsweredSteps.includes(step))
          : steps;
      };
    },

    isStageCompteled() {
      return (stageName: PropertyOnboardingStage) => {
        const steps = this.getStageSteps(stageName);
        const stepsCount = steps?.length;

        if (!steps || !stepsCount) {
          return false;
        }

        const completedSteps = steps.map((step) => this.isStepCompleted(step)).filter((isCompleted) => isCompleted);

        return completedSteps.length === stepsCount;
      };
    },

    isStepCompleted() {
      return (stepName: PropertyOnboardingStep) => {
        const { property } = usePropertyStore();

        return stepValidations[stepName](property);
      };
    },
  },
  actions: {
    isFirstStage(stage: PropertyOnboardingStage) {
      return stage === this.getStages[0];
    },

    isLastStage(stage: PropertyOnboardingStage) {
      return stage === this.getStages[this.getStages.length - 1];
    },

    isFirstStep(stage: PropertyOnboardingStage, step: PropertyOnboardingStep) {
      const steps = this.getStageSteps(stage);

      return steps[0] === step;
    },

    isLastStep(stage: PropertyOnboardingStage, step: PropertyOnboardingStep) {
      const steps = this.getStageSteps(stage);

      return steps[steps.length - 1] === step;
    },

    getNextStep(stage: PropertyOnboardingStage, step: PropertyOnboardingStep): PropertyOnboardingStep {
      const steps = this.getStageSteps(stage);
      const currentStepIndex = steps.findIndex((name) => name === step);

      return steps[currentStepIndex + 1];
    },

    getPrevStep(stage: PropertyOnboardingStage, step: PropertyOnboardingStep): PropertyOnboardingStep {
      const steps = this.getStageSteps(stage);
      const currentStepIndex = steps.findIndex((name) => name === step);

      return steps[currentStepIndex - 1];
    },

    navigateToStep(step: PropertyOnboardingStep) {
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.property?.id;
      // Until the unit exists, the portfolio it was started from travels with the flow to the address step.
      // The store holds the router reactively, which unwraps currentRoute; unref reads it either way
      const portfolio = propertyId ? undefined : unref(this.router.currentRoute).query.portfolio;

      return this.router.push({
        name: getPropertyOnboardingStepRouteName(step),
        params: {
          propertyId: propertyId ?? PropertyOnboardingType.NewProperty,
        },
        query: typeof portfolio === 'string' ? { portfolio } : undefined,
      });
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePropertyOnboardingStore, import.meta.hot));
}
